import { useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, FormText } from "reactstrap";

import { HomeCover } from "../../models/homeCoverDto";
import ButtonComponent from "../../pages/dashboard/training-path/components/buttonComponent";
import { useHttpClient } from "../../security/hooks/axiosProvider";
import styles from "./cover-modal.module.scss";
import { translate } from "react-switch-lang";

const CoverModal: React.FC<{ t: (key: string) => {}, coverModalState: boolean; setIsCoverModalOpened: any; setHomeCover: any; setIsDone: any }> = ({ t, coverModalState, setIsCoverModalOpened, setHomeCover, setIsDone }) => {
  const httpClient = useHttpClient(true);
  const [titre, setTitre] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [coverType, setCoverType] = useState<string>("CAROUSEL");
  const [selectedFile, setSelectedFile] = useState<File>();
  const [preview, setPreview] = useState<string>();
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [previews, setPreviews] = useState<Array<string>>([]);

  useEffect(() => {
    if (selectedFiles.length === 0) {
      setPreviews([]);
      return;
    }

    let urls = [];
    for (let file of selectedFiles) {
      const multipleObjectUrl = URL.createObjectURL(file);
      urls.push(multipleObjectUrl);
      setPreviews(urls);
    }

    return () => {
      for (let url of urls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [selectedFiles]);

  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const onSelectFile = event => {
    if (!event.target.files || event.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }
    setSelectedFile(event.target.files[0]);
  };

  const onSelectFiles = event => {
    if (!event.target.files || event.target.files.length === 0) {
      setSelectedFiles([]);
      return;
    }
    setSelectedFiles(event.target.files);
  };

  const onToggleCoverModal = () => {
    setIsCoverModalOpened(!coverModalState);
    resetCarouselForm();
    resetTextForm();
  };

  const resetCarouselForm = () => {
    setSelectedFiles([]);
  };

  const resetTextForm = () => {
    setTitre(null);
    setDescription(null);
    setSelectedFile(undefined);
  };

  const onChangeCoverType = event => {
    const { value } = event.target;
    setCoverType(value);
    resetTextForm();
    resetCarouselForm();
  };

  const onChangeTitleInputHandler = event => {
    setTitre(event.target.value);
  };

  const onChangeDescriptionInputHandler = event => {
    setDescription(event.target.value);
  };

  const onSingleImageFormSubmissionHandler = event => {
    event.preventDefault();
    setIsCoverModalOpened(!coverModalState);
    setIsDone(false);

    let homeCover: HomeCover = {
      id: null,
      titre: titre,
      description: description,
      type: null,
      homeCoverImages: [],
    };

    if (selectedFile && !description) {
      homeCover.type = "IMAGE";
    } else if (description) {
      homeCover.type = "TEXT";
    }

    httpClient.current
      ?.post("/homeCover", homeCover, {
        headers: { "Content-Type": "application/json" },
      })
      .then(async response => {
        if (selectedFile) {
          let formData: FormData = new FormData();
          formData.append("file", selectedFile);
          formData.append("homeCoverID", response.data);
          await httpClient.current
            ?.post("/homeCoverImage", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then(image => {
              homeCover.homeCoverImages.push({ url: image.data });
              setHomeCover(homeCover);
            });
        } else {
          setHomeCover(homeCover);
        }
        setIsDone(true);
        onToggleCoverModal();
      });
  };

  const onMultipleImagesFormSubmissionHandler = event => {
    setIsCoverModalOpened(!coverModalState);
    setIsDone(false);
    event.preventDefault();

    if (selectedFiles.length > 1) {
      const homeCover: HomeCover = {
        id: null,
        titre: null,
        description: null,
        type: "CAROUSEL",
        homeCoverImages: [],
      };

      httpClient.current
        ?.post("/homeCover", homeCover, {
          headers: { "Content-Type": "application/json" },
        })
        .then(async response => {
          for (let file of selectedFiles) {
            let formData: FormData = new FormData();
            formData.append("file", file);
            formData.append("homeCoverID", response.data);
            await httpClient.current
              ?.post("/homeCoverImage", formData, {
                headers: { "Content-Type": "multipart/form-data" },
              })
              .then(images => {
                homeCover.homeCoverImages.push({ url: images.data });
              });
          }
          homeCover.id = response.data;
          setHomeCover(homeCover);
          setIsDone(true);
          onToggleCoverModal();
        });
    }
  };

  return (
    <>
      <Modal isOpen={coverModalState} toggle={onToggleCoverModal} className={styles["cover-modal"]}>
        <ModalHeader toggle={onToggleCoverModal}>{t("training_path_cover")}</ModalHeader>
        <ModalBody>
          <FormGroup tag="fieldset">
            <legend className={styles["cover-modal__cover-type"]}>{t("choose_type_of_cover")}</legend>
            <FormGroup check>
              <Label check>
                <Input checked={coverType === "CAROUSEL"} type="radio" name="cover-type-radio" value="CAROUSEL" onChange={onChangeCoverType} />
                {t("carousel")}
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input checked={coverType === "TEXT"} type="radio" name="cover-type-radio" value="TEXT" onChange={onChangeCoverType} />
                {t("image_with_text")}
              </Label>
            </FormGroup>
          </FormGroup>

          <hr />

          {coverType === "CAROUSEL" && (
            <form onSubmit={onMultipleImagesFormSubmissionHandler}>
              <h6>{t("select_images_from_carousel")}</h6>
              <FormGroup className={styles["modal-input"]}>
                <Input type="file" name="multiple-files" multiple accept="image/*" onChange={onSelectFiles} />
                <FormText color="muted">{t("select_at_least_two_images")}</FormText>
              </FormGroup>
              <div className={styles["cover-multiple-images"]}>
                {selectedFiles &&
                  previews.map((preview, index) => (
                    <img key={index} src={preview} alt="cover-preview" className={styles["cover-image-preview"].concat(" ").concat(styles["margin-multiple-images"])} />
                  ))}
              </div>
              <hr />
              <div className={styles["modal-actions"]}>
                <ButtonComponent disable={selectedFiles.length < 2} buttonText={"Enregistrer"} buttonType={"submit"} save={true} />
                <ButtonComponent onClick={onToggleCoverModal} disable={false} buttonText={(t("cancel")).toString()} buttonType={"button"} save={false} />
                <Button color="link" type="reset" onClick={resetCarouselForm}>
                {t("empty")}
                </Button>
              </div>
            </form>
          )}

          {coverType === "TEXT" && (
            <form onSubmit={onSingleImageFormSubmissionHandler}>
              <h6>{t("add_image_or_description")}</h6>
              <FormGroup className={styles["modal-input"]}>
                <Input type="file" name="single-file" accept="image/*" onChange={onSelectFile} />
                <FormText color="muted">{t("select_single_image")}</FormText>
              </FormGroup>
              <div className={styles["cover-single-image"]}>{selectedFile && <img src={preview} alt="cover-preview" className={styles["cover-image-preview"]} />}</div>
              <FormGroup>
                <Input type="text" placeholder="Titre" onInput={onChangeTitleInputHandler} />
              </FormGroup>
              <FormGroup className={styles["modal-input"]}>
                <Input type="textarea" required={!selectedFile} placeholder="Description" onInput={onChangeDescriptionInputHandler} />
              </FormGroup>
              <hr />
              <div className={styles["modal-actions"]}>
                <ButtonComponent disable={!selectedFile} buttonText={"Enregistrer"} buttonType={"submit"} save={true} />
                <ButtonComponent onClick={onToggleCoverModal} disable={false} buttonText={(t("cancel")).toString()} buttonType={"button"} save={false} />
                <Button color="link" type="reset" onClick={resetTextForm}>
                {t("empty")}
                </Button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default translate(CoverModal);
